import { IDisposable, IEventAggregator, disposableSubscription, compositeDisposable, emptyDisposable, disposable } from 'common/barrels/core';
import { Subject, Observable } from 'common/barrels/rx';
import { Request } from 'common/request';
import * as path from 'shared/path';
import { Set, Map } from 'immutable';
import { camel } from 'common/functions/string';
import { emptyGuid } from 'common/functions/guid';
import { DataRequest } from 'shared/data/datarequest';

import { Control } from 'runtime/controls/control';
import { BindingInfo } from 'runtime/metadata/bindinginfo';
import { FieldDataUpdate } from 'runtime/data/fielddataupdate';
import { LookupItem } from 'runtime/data/lookupitem';
import { CallQueue } from 'runtime/callqueue';
import { RequestTracker } from 'runtime/requesttracker';
import { FieldDataRequest } from 'runtime/data/fielddatarequest';
import { RuleDataRequest } from 'runtime/data/ruledatarequest';
import { RuleDataUpdate } from 'runtime/data/ruledataupdate';
import { LookupItemUpdate } from 'runtime/data/lookupitemupdate';
import { FileFieldMetadata } from 'runtime/data/filefieldmetadata';
import { FieldDataSecurity } from 'runtime/data/fielddatasecurity';
import { DataRequestStarted } from 'runtime/events/datarequeststarted';
import { DataRequestFinished } from 'runtime/events/datarequestfinished';
import { SessionError } from 'runtime/messages/sessionerror';
import { WorkItemViewInfo } from 'runtime/data/workitemviewinfo';

import { Serializer } from 'common/serializer';
import { SessionDataUpdate } from './data/sessiondataupdate';

export interface IValueStore extends IDisposable {
    createBinding(binding: BindingInfo, control: Control, $element: JQuery, context: string): IDisposable;
    bindSecurity(context: string, fieldName: string, lambda: (value: boolean) => void): IDisposable;
    update(type: string, identifier: string, name: string, value: any);
    bindOneWay(type: string, identifier: string, name: string, callback): IDisposable;
    getLookupItem(identifier: string, fieldName: string, keyValue: string);
    getUpdates(): (FieldDataUpdate|SessionDataUpdate)[]
    processUpdates(updates: DataRequest[]);
    withValue(type: string, identifier: string, name: string, call: (x) => void);

    markAsClean(cleanKeys: FieldDataUpdate[]);
    allValuesDirty();
    rerequestAllRules();
    autoClaimed(): boolean;
}

export class ValueStore implements IValueStore {
    private wivi: WorkItemViewInfo;
    private callQueue: CallQueue;
    private eventAggregator: IEventAggregator;
    private dirtyValues = Set<string>();
    private values = Map<string, any>();
    private requestedValues = new Subject<string>();
    private requestedValuesSubscription: IDisposable;
    private bindings = Map<string, any>();
    private adHocValueRequests = Map<string, ((x: any) => void)[]>();
    private lookupItemCache = Map<string, LookupItem>();
    private disposed = false;
    private serializer: Serializer;

    constructor(wivi: WorkItemViewInfo, requests: RequestTracker, callQueue: CallQueue, eventAggregator: IEventAggregator, serializer: Serializer, csrfToken: () => string) {
        this.wivi = wivi;
        this.callQueue = callQueue;
        this.eventAggregator = eventAggregator;
        this.serializer = serializer;

        this.processUpdates(wivi.initialDataChanges);

        this.values = this.values.set(this.key('Variable', emptyGuid, 'IsReadOnly'), this.wivi.workItem.readOnly);
        this.values = this.values.set(this.key('Variable', emptyGuid, 'IsNotReadOnly'), !this.wivi.workItem.readOnly);

        this.requestedValuesSubscription = disposableSubscription(this.requestedValues.bufferWhen(() => Observable.timer(50)).filter(x => x.length > 0).subscribe(x => {
            let rerequestedRules = Set<string>();
            let valueRequests = Set(x);

            var processUpdates = (t: DataRequest[]) => {
                this.ifNotDisposed(() => {
                    if (requests.current() === 0)
                        requests.increment();

                    this.processUpdates(t);
                });    
            };

            this.callQueue.enqueue(() => this.ifNotDisposed(() => new Request(this.serializer, path.ui('data'))
                .before(() => this.eventAggregator.publish(new DataRequestStarted()))
                .args(() => ({
                    cacheToken: this.wivi.cacheToken,
                    workflowInstanceId: this.wivi.workItem.workflowInstanceId,
                    workItemId: this.wivi.workItem.workItemId,
                    versionId: this.wivi.workItem.versionId,
                    readOnly: this.wivi.workItem.readOnly,
                    freshlyCreated: this.wivi.workItem.freshlyCreated,
                    nextRequestId: requests.current(),
                    definitionType: this.wivi.workItem.definitionName,
                    requestedData: valueRequests.union(rerequestedRules).map(y => this.createDataRequest(y)).toArray(),
                    dataChanges: this.getUpdates()
                }))
                .handleArray(DataRequest, t => processUpdates(t))
                .handle(null, () => processUpdates([]))
                .retry(SessionError, () => this.ifNotDisposed(() => {
                    requests.reset();
                    this.allValuesDirty();
                    rerequestedRules = Set(this.values.keySeq().filter(x => this.isRule(x)).toArray());
                }))
                .final(() => this.ifNotDisposed(() => {
                    this.eventAggregator.publish(new DataRequestFinished());
                    this.callQueue.proceed();
                }))
                .post(csrfToken())
            ));
        }));
    }

    autoClaimed(): boolean {
        return this.wivi.workItem.autoClaimed;
    }

    private createDataRequest(key: string): DataRequest {
        var elements = this.keyElements(key);

        if (elements.type === 'Field')
            return new FieldDataRequest(elements.identifier, elements.name);

        if (elements.type === 'Rule')
            return new RuleDataRequest(elements.identifier, elements.name);

        throw new Error('Unknown value request type: ' + elements.type);
    }

    processUpdates(updates: DataRequest[]) {
        (updates || []).forEach(x => {
            if (x instanceof FieldDataUpdate)
                this.updateInternal(this.key('Field', x.identifier, x.fieldName), x.value);

            if (x instanceof RuleDataUpdate)
                this.updateInternal(this.key('Rule', x.identifier, x.ruleName), x.value);

            if (x instanceof LookupItemUpdate)
                this.storeLookupItem(x.identifier, x.fieldName, x.lookupItem);

            if (x instanceof FileFieldMetadata)
                this.updateInternal(this.key('FileFieldMetadata', x.identifier, x.fieldName), x);

            if (x instanceof FieldDataSecurity)
                this.updateInternal(this.key('FieldSecurity', x.identifier, x.fieldName), x.canWrite);

            if (x instanceof SessionDataUpdate)
                this.updateInternal(this.key('SessionData', undefined, x.id), x);
        });
    }

    dispose() {
        this.requestedValuesSubscription.dispose();
        this.requestedValues.unsubscribe();
        this.disposed = true;
    }

    ifNotDisposed(lambda: () => void) {
        if (!this.disposed)
            lambda();
    }

    getUpdates(): (FieldDataUpdate|SessionDataUpdate)[] {
        const createSessionDataUpdate = (sd: SessionDataUpdate) => {
            return SessionDataUpdate.create(undefined, sd.data, sd.keyId, sd.iv);
        }

        return this.dirtyValues.toArray()
            .map(x => this.keyElements(x))
            .map(x => x.type === 'Field' ? FieldDataUpdate.create(x.identifier, x.name, this.values.get(this.key('Field', x.identifier, x.name)))
                    : x.type === 'SessionData' ? createSessionDataUpdate(this.values.get(this.key('SessionData', x.identifier, x.name)))
                    : undefined
            );
    }

    markAsClean(cleanKeys: (FieldDataUpdate|SessionDataUpdate)[]) {
        const keyAndValue = (x: FieldDataUpdate | SessionDataUpdate) => x instanceof SessionDataUpdate
                  ? { key: this.key('SessionData', undefined, x.id), value: x} 
                  : { key: this.key('Field', x.identifier, x.fieldName), value: x.value};

        cleanKeys.map(x => keyAndValue(x)).forEach(x => {
            if (this.dirtyValues.contains(x.key) && this.values.get(x.key) === x.value)
                this.dirtyValues = this.dirtyValues.remove(x.key);
        });
    }

    allValuesDirty() {
        this.dirtyValues = Set(this.values.keySeq().filter(x => this.isField(x) || this.isSessionData(x)).toArray());
    }

    rerequestAllRules() {
        this.values.keySeq().filter(x => this.isRule(x)).forEach(x => this.requestValue(x));
    }

    private key(type: string, identifier: string, name: string): string {
        return `${type}:${type === 'Variable' ? emptyGuid : identifier}:${name}`;
    }

    private keyElements(key: string): { type: string; identifier: string; name: string } {
        var parts = key.split(':');
        return {
            type: parts[0],
            identifier: parts[1],
            name: parts[2]
        };
    }

    private isField(key: string) {
        return this.keyElements(key).type === 'Field';
    }

    private isRule(key: string) {
        return this.keyElements(key).type === 'Rule';
    }

    private isSessionData(key: string) {
        return this.keyElements(key).type === 'SessionData';
    }

    bindSecurity(context: string, fieldName: string, lambda: (value: boolean) => void): IDisposable {
        var key = this.key('FieldSecurity', context, fieldName);
        var bs = this.bindings.get(key) || [];
        bs.push(lambda);
        this.bindings = this.bindings.set(key, bs);

        if (this.values.has(key))
            lambda(this.values.get(key));

        return disposable(() => bs.splice(bs.indexOf(lambda), 1));
    }

    createBinding(binding: BindingInfo, control: Control, $element: JQuery, potentiallyIgnoredContext: string): IDisposable {
        var invertIfNecessary = x => (x === null || binding.parameter !== 'invert') ? x : !x;

        const context = binding.ignoreFieldContext === 'true' ? emptyGuid : potentiallyIgnoredContext;

        var key = this.key(binding.type, context, binding.source);
        var bs = this.bindings.get(key) || [];
        var target = camel(binding.target);
        var unsubscriptionHooks: IDisposable[] = [];

        if (binding.mode === 'OneTime') {
            if (this.values.has(key)) {
                control[target](invertIfNecessary(this.values.get(key)));
            } else {
                this.addToAdHocRequests(key, x => control[target](invertIfNecessary(x)));
                this.requestValue(key);
            }
        }

        if (binding.mode === 'TwoWay') {
            var controlToStore = (e, p) => this.update(binding.type, context, binding.source, invertIfNecessary(p));
            $element.on(target, controlToStore);
            unsubscriptionHooks.push(disposable(() => $element.unbind(target, <any>controlToStore)));
        }

        if (binding.mode === 'OneWay' || binding.mode === 'TwoWay') {
            var storeToControl = x => control[target](invertIfNecessary(x));
            bs.push(storeToControl);
            this.bindings = this.bindings.set(key, bs);
            unsubscriptionHooks.push(disposable(() => bs.splice(bs.indexOf(storeToControl), 1)));

            if (this.values.has(key)) {
                control[target](invertIfNecessary(this.values.get(key)));
            } else {
                this.requestValue(key);
            }
        }

        return compositeDisposable(unsubscriptionHooks);
    }

    private addToAdHocRequests(key: string, f: (x: any) => void) {
        const n = (this.adHocValueRequests.get(key) || []).slice();
        n.push(f);
        this.adHocValueRequests = this.adHocValueRequests.set(key, n);
    }

    bindOneWay(type: string, identifier: string, name: string, callback): IDisposable {
        var key = this.key(type, identifier, name);
        var bs = this.bindings.get(key) || [];

        var call = x => callback(x, identifier);
        bs.push(call);

        this.bindings = this.bindings.set(key, bs);

        if (this.values.has(key)) {
            call(this.values.get(key));
        } else {
            // certain types come down with the security metadata always, and should not require manual requesting. This may change, but is true with the current server sync infrastructure
            if (type !== 'FileFieldMetadata')
                this.requestValue(key);
        }

        return disposable(() => bs.splice(bs.indexOf(call), 1));
    }

    private updateInternal(key: string, value: any) {
        this.values = this.values.set(key, value);
        (this.bindings.get(key) || []).forEach(x => x(value));
        (this.adHocValueRequests.get(key) || []).forEach(x => x(value));
        this.adHocValueRequests = this.adHocValueRequests.remove(key);
    }

    update(type: string, identifier: string, name: string, value: any) {
        var key = this.key(type, identifier, name);
        if (this.values.get(key) === value)
            return;

        if (type === 'Field')
            this.dirtyValues = this.dirtyValues.add(key);

        this.updateInternal(key, value);
    }

    private lookupKey(identifier: string, fieldName: string, keyValue: string) {
        return `${identifier}:${fieldName}:${keyValue}`;
    }

    private storeLookupItem(identifier: string, fieldName: string, item: LookupItem) {
        if (item === null)
            return;

        this.lookupItemCache = this.lookupItemCache.set(this.lookupKey(identifier, fieldName, item.keyValue), item);
    }

    getLookupItem(identifier: string, fieldName: string, keyValue: string) {
        return this.lookupItemCache.get(this.lookupKey(identifier, fieldName, keyValue));
    }

    withValue(type: string, identifier: string, name: string, call: (x) => void) {
        var key = this.key(type, identifier, name);
        if (this.values.has(key)) {
            call(this.values.get(key));
        } else {
            this.addToAdHocRequests(key, call);
            this.requestValue(key);
        }
    }

    private requestValue(key) {
        this.ifNotDisposed(() => this.requestedValues.next(key));
    }
}

// CLEAN UP
export class MockValueStore implements IValueStore {
    createBinding(binding: BindingInfo, control: Control, $element: JQuery, context: string): IDisposable {
        return emptyDisposable;
    }

    bindSecurity(context: string, fieldName: string, lambda: (value: boolean) => void): IDisposable {
        return emptyDisposable;
    }

    update(type: string, identifier: string, name: string, value) { }

    bindOneWay(type: string, identifier: string, name: string, callback): IDisposable {
        return emptyDisposable;
    }

    dispose(): void { }

    getLookupItem(identifier: string, fieldName: string, keyValue: string) { }

    getUpdates(): FieldDataUpdate[] { return []; }

    processUpdates(updates: DataRequest[]) { }

    withValue(type: string, identifier: string, name: string, call: (x) => void) { }

    markAsClean(cleanKeys: FieldDataUpdate[]) { }

    allValuesDirty() { }

    rerequestAllRules() { }

    autoClaimed() { return false; }
}